/*
  Files tab, wave 3 (UI overhaul screen 06 ideas 4–8):
  - useFilesScans      — the stored scans (GET /projects/{id}/files-scans): the last folder tree + history, so the
                         tab shows its health without scanning again (idea 4)
  - FilesHealthCard    — ring "17/25" + Ready / Empty / Missing + "Create 3 missing folders" (ideas 4, 6)
  - StageEvidenceCard  — the stages each main folder proves, with that folder's completeness (idea 5)
  - FolderPreviewCard  — the selected required folder's files on OneDrive + a drop zone that uploads into it (idea 7).
                         A file uploaded from Sara shows who really uploaded it (OneDrive names whoever connected
                         the project). On phones / tablets the card sits below the 25-row list, so picking a
                         folder scrolls it into view.
  - ScanHistoryCard    — manual and nightly scans with their ready count (ideas 4, 8), plus the uploads and new
                         folders made from Sara (`activity`); "nightly auto-scan on" only once the job really ran.
  Everything that calls Microsoft is off unless the OneDrive integration is on (useFeature('onedrive')); the
  stored results still show.
*/
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, CalendarClock, ExternalLink, FileText, FileUp, Folder, FolderOpen, FolderPlus, Link2, Loader2, RefreshCcw } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatDateTime } from '@/lib/format';
import { formatFileSize, uploadFile } from '@/lib/upload';
import { isFeatureNotEnabledError } from '@/lib/integrations';
import { Button } from '@/components/ui/button';
import { ProgressRing } from '@/components/ui/progress-bar';
import { StatusPill, ToneDot } from '@/components/ui/status-pill';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { NotConfigured } from '@/components/ui/not-configured';
import { Avatar } from '@/components/ui/avatar-initials';
import { Panel, PanelHead } from './ProjectUi';
import { FOLDER_STATUS, FOLDER_UPLOAD_ACCEPT, FOLDER_UPLOAD_MAX_BYTES, fileKindLabel } from './filesModel';

/** Stored scans of a project (no call to Microsoft). */
export function useFilesScans(projectId, { enabled = true, refreshKey } = {}) {
  const [state, setState] = useState({ loading: Boolean(enabled), data: null, error: '' });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((value) => value + 1), []);
  useEffect(() => {
    if (!enabled || !projectId) {
      setState({ loading: false, data: null, error: '' });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: !prev.data, error: '' }));
    api.get(`/projects/${projectId}/files-scans`, { params: { limit: 5 } })
      .then((res) => { if (!cancelled) setState({ loading: false, data: res.data || null, error: '' }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, data: null, error: getErrorMessage(err, 'Could not load the last scan') }); });
    return () => { cancelled = true; };
  }, [projectId, enabled, refreshKey, tick]);
  return { ...state, reload };
}

// The ring is the READY share (green, as approved); the legend and "N folders need attention" carry the problems.
// Red only when nothing is ready at all.
const RING_TONE = (health) => (health.total > 0 && health.ready === 0 ? 'red' : 'green');

export function FilesHealthCard({ health, scanned, handover, onCreateMissing, creating = false, createState, className }) {
  const attention = health.empty + health.missing;
  return (
    <Panel className={cn('flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-4 sm:px-5', className)} data-testid="files-health">
      <ProgressRing
        value={scanned ? health.percent : 0}
        tone={scanned ? RING_TONE(health) : 'gray'}
        size={72}
        thickness={7}
        centerLabel={scanned ? `${health.ready}/${health.total}` : '—'}
        label={scanned ? `${health.ready} of ${health.total} required folders ready` : 'Folder health not scanned yet'}
        className="text-[15px] font-extrabold"
      />
      <div className="min-w-[200px] flex-1">
        <h3 className="text-[15px] font-bold text-ink">Folder health</h3>
        {scanned ? (
          <>
            <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ink-2" aria-label="Required folders by status">
              <li className="inline-flex items-center gap-1.5"><ToneDot tone="green" />Ready <b className="tabular-nums text-ink">{health.ready}</b></li>
              <li className="inline-flex items-center gap-1.5"><ToneDot tone="amber" />Empty <b className="tabular-nums text-ink">{health.empty}</b></li>
              <li className="inline-flex items-center gap-1.5"><ToneDot tone="red" />Missing <b className="tabular-nums text-ink">{health.missing}</b></li>
            </ul>
            <p className="mt-1 text-[12.5px] text-ink-3">
              {attention ? `${attention} folder${attention === 1 ? ' needs' : 's need'} attention` : 'Every required folder has files'}
            </p>
            {handover?.overdue && attention ? (
              <p className="mt-0.5 text-[12.5px] font-semibold text-status-red-ink">
                Handover was due {formatDate(handover.endDate)} ({handover.days} d overdue)
              </p>
            ) : null}
          </>
        ) : (
          <p className="mt-1 text-[12.5px] text-ink-3">No scan yet — the ring fills after the first scan (Scan OneDrive, or the nightly scan once OneDrive is set up).</p>
        )}
      </div>
      {/* 28 #8: no onCreateMissing (a role whose Files module is View only) → no button at all */}
      {scanned && health.missing > 0 && onCreateMissing ? (
        <div className="flex flex-col items-end gap-1">
          <Button
            type="button"
            variant="outline"
            onClick={onCreateMissing}
            disabled={creating || !createState?.allowed}
            title={createState?.allowed ? undefined : createState?.reason}
            aria-describedby={createState?.allowed ? undefined : 'files-create-missing-note'}
            data-testid="files-create-missing"
          >
            {creating ? <Loader2 className="animate-spin" aria-hidden="true" /> : <FolderPlus aria-hidden="true" />}
            Create {health.missing} missing folder{health.missing === 1 ? '' : 's'}
          </Button>
          {!createState?.allowed && createState?.reason ? (
            <span id="files-create-missing-note" className="max-w-[260px] text-right text-[11.5px] leading-snug text-ink-3">{createState.reason}</span>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

export function StageEvidenceCard({ evidence = [], onOpenStage, className }) {
  if (!evidence.length) return null;
  return (
    <Panel className={cn('overflow-hidden', className)} data-testid="files-stage-evidence" aria-labelledby="files-stage-evidence-title">
      <PanelHead title={<span id="files-stage-evidence-title">Stage evidence</span>} meta={`${evidence.length} linked folder${evidence.length === 1 ? '' : 's'}`} />
      <ul>
        {evidence.map((entry) => (
          <li key={entry.folder} className="border-b border-line-2 px-4 py-2.5 last:border-b-0 sm:px-5" data-testid={`stage-evidence-${entry.stage.id}`}>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onOpenStage?.(entry.stage)}
                className="min-w-0 flex-1 truncate rounded text-left text-[13px] font-semibold text-ink hover:text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="mr-1 font-mono text-[11.5px] text-ink-3">{entry.number}</span>
                {entry.stage.name}
              </button>
              <StatusPill status={entry.stage.status || 'Not Started'} domain="stage" size="sm" />
            </div>
            <p className="mt-0.5 flex items-center gap-1.5 text-[12px] text-ink-3">
              <Folder className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{entry.folder}</span>
              <span aria-hidden="true">·</span>
              <span className={cn('font-semibold', entry.tone === 'green' && 'text-status-green-ink', entry.tone === 'amber' && 'text-status-amber-ink', entry.tone === 'red' && 'text-status-red-ink')}>
                {entry.note}
              </span>
            </p>
            {entry.warn ? (
              <p className="mt-1 text-[12px] font-semibold text-status-amber-ink" role="note">
                Stage is approved but its folder is not complete — the proof is missing.
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function fileMeta(file) {
  return [
    fileKindLabel(file.name, file.mime_type),
    file.size ? formatFileSize(file.size) : '',
    file.modified_at ? formatDate(file.modified_at) : '',
    // uploaded from Sara: the real person (OneDrive itself names whoever connected the project)
    file.uploaded_by ? `uploaded by ${file.uploaded_by}` : (file.modified_by || ''),
  ].filter(Boolean).join(' · ');
}

/** Below the lg breakpoint the preview card is under the folder list (the page's grid stacks). */
const isStacked = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(max-width: 1023.98px)').matches;

/**
 * The files of one required folder (06 #7). `row` is the selected checklist row; `linked` = its stage link
 * ("Linked to Stage 05 · BOQ & Design → C. Variations").
 */
export function FolderPreviewCard({ projectId, row, linked, onedriveOn, connected, canWrite, canEdit = true, onOpenUrl, onUploaded, className }) {
  const [state, setState] = useState({ loading: false, files: [], error: '', notEnabled: false });
  const path = row?.path || '';
  const canBrowse = Boolean(path && onedriveOn && connected && row?.status !== 'missing');
  const cardRef = useRef(null);
  const shownPathRef = useRef(path);

  // Phones / tablets: the card is far below the folder list — bring it into view when a folder is picked
  // (not on the first render, so opening the tab never jumps).
  useEffect(() => {
    if (path === shownPathRef.current) return;
    shownPathRef.current = path;
    if (!path || !isStacked()) return;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    cardRef.current?.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, [path]);

  useEffect(() => {
    if (!canBrowse) {
      setState({ loading: false, files: [], error: '', notEnabled: false });
      return undefined;
    }
    let cancelled = false;
    setState({ loading: true, files: [], error: '', notEnabled: false });
    api.get(`/projects/${projectId}/onedrive/folder`, { params: { path } })
      .then((res) => { if (!cancelled) setState({ loading: false, files: res.data?.files || [], error: '', notEnabled: false }); })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, files: [], error: getErrorMessage(err, 'Could not read this folder'), notEnabled: isFeatureNotEnabledError(err) });
      });
    return () => { cancelled = true; };
  }, [projectId, path, canBrowse]);

  if (!row && !onedriveOn) {
    return (
      <Panel ref={cardRef} className={cn('scroll-mt-[calc(var(--app-header-height,64px)+12px)] p-4 sm:px-5', className)} data-testid="folder-preview">
        <h3 className="mb-3 text-[14px] font-bold text-ink">Folder files</h3>
        <NotConfigured feature="onedrive" text="Browsing, uploading and creating folders need the OneDrive connection. The folder list shows the last scan." testId="folder-preview-not-configured" />
      </Panel>
    );
  }
  if (!row) {
    return (
      <Panel ref={cardRef} className={cn('scroll-mt-[calc(var(--app-header-height,64px)+12px)] px-5 py-8 text-center', className)} data-testid="folder-preview">
        <FolderOpen className="mx-auto h-6 w-6 text-ink-4" aria-hidden="true" />
        <p className="mt-2 text-[13px] font-semibold text-ink">Pick a folder</p>
        <p className="mt-0.5 text-[12.5px] text-ink-3">Its latest files and an upload box appear here.</p>
      </Panel>
    );
  }

  const [parent, name] = row.path.includes('/') ? [row.path.slice(0, row.path.lastIndexOf('/')), row.name] : ['', row.name];
  const meta = FOLDER_STATUS[row.status] || FOLDER_STATUS.not_scanned;
  const files = state.files.filter((file) => !file.is_folder);
  const uploadPath = row.path;

  return (
    <Panel ref={cardRef} className={cn('scroll-mt-[calc(var(--app-header-height,64px)+12px)] overflow-hidden', className)} data-testid="folder-preview" aria-labelledby="folder-preview-title">
      <div className="border-b border-line px-4 py-3 sm:px-5">
        <div className="flex items-start gap-2">
          <h3 id="folder-preview-title" className="min-w-0 flex-1 text-[13.5px] text-ink-3">
            {parent ? <span>{parent.split('/').join(' › ')} › </span> : null}
            <b className="font-bold text-ink">{name}</b>
          </h3>
          <StatusPill tone={meta.tone} label={meta.label} size="sm" />
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[12px] text-ink-3">
          <span>{canBrowse && !state.loading && !state.error ? `${files.length} file${files.length === 1 ? '' : 's'}${files[0]?.modified_at ? ` · updated ${formatDate(files[0].modified_at)}` : ''}` : (row.fileCount !== null ? `${row.fileCount} file${row.fileCount === 1 ? '' : 's'} at the last scan` : 'Not scanned yet')}</span>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {/* phones / tablets: back up to the folder list this card was scrolled away from */}
            <button
              type="button"
              onClick={() => {
                const target = [...document.querySelectorAll('[data-testid^="folder-row-"]')].find((element) => element.dataset.testid === `folder-row-${row.path}`);
                target?.scrollIntoView?.({ block: 'center' });
                target?.querySelector('button')?.focus?.({ preventScroll: true });
              }}
              className="inline-flex items-center gap-1 font-semibold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
              data-testid="folder-preview-back"
            >
              <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
              Folders
            </button>
            {row.webUrl ? (
              <button type="button" onClick={() => onOpenUrl?.(row.webUrl)} className="inline-flex items-center gap-1 font-semibold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                Open in OneDrive
              </button>
            ) : null}
          </span>
        </div>
      </div>

      {!onedriveOn ? (
        <div className="p-4 sm:px-5">
          <NotConfigured feature="onedrive" text="Browsing and uploading project files needs the OneDrive connection. The folder list on the left still shows the last scan." testId="folder-preview-not-configured" />
        </div>
      ) : !connected ? (
        <p className="px-4 py-6 text-center text-[13px] text-ink-3 sm:px-5">
          {canEdit
            ? 'Connect OneDrive for this project to see and upload its files.'
            : 'OneDrive is not connected for this project — ask someone with Files edit access to connect it.'}
        </p>
      ) : row.status === 'missing' ? (
        <p className="px-4 py-6 text-center text-[13px] text-ink-3 sm:px-5">This folder does not exist on OneDrive yet{canEdit ? ' — create the missing folders first' : ''}.</p>
      ) : (
        <>
          {state.loading ? (
            <div className="space-y-2 p-4 sm:px-5" role="status" aria-label="Loading files">
              {[0, 1, 2].map((index) => <span key={index} className="ui-skeleton-shimmer block h-11 rounded-lg" />)}
            </div>
          ) : state.error ? (
            <p className="px-4 py-4 text-[13px] text-status-red-ink sm:px-5" role="alert">{state.error}</p>
          ) : files.length ? (
            <ul className="max-h-[320px] overflow-y-auto" data-testid="folder-preview-files">
              {files.slice(0, 50).map((file) => (
                <li key={file.id || file.name} className="flex items-center gap-3 border-b border-line-2 px-4 py-2.5 last:border-b-0 sm:px-5">
                  <span className="ui-icon-tile h-8 w-8 shrink-0 bg-status-violet-bg text-status-violet-ink" aria-hidden="true"><FileText className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => file.web_url && onOpenUrl?.(file.web_url)}
                      className="block max-w-full truncate rounded text-left text-[13px] font-semibold text-ink hover:text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      title={file.name}
                    >
                      {file.name}
                    </button>
                    <span className="block truncate text-[12px] text-ink-3">{fileMeta(file)}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-5 text-center text-[13px] text-ink-3 sm:px-5">No files in this folder yet.</p>
          )}
          <div className="border-t border-line p-4 sm:px-5">
            {canWrite ? (
              <FileDropzone
                accept={FOLDER_UPLOAD_ACCEPT}
                maxSize={FOLDER_UPLOAD_MAX_BYTES}
                label={`Drop files to upload to ${name}`}
                hint={(
                  <>
                    <span className="font-semibold text-[color:var(--brand-ink)] underline underline-offset-2">or browse</span>
                    {' · PDF, DWG, Excel, Word, images up to 50 MB'}
                  </>
                )}
                upload={(file, { onProgress, signal }) => uploadFile(`/projects/${projectId}/onedrive/folder/upload`, file, { fields: { path: uploadPath }, onProgress, signal })}
                onUploaded={(result) => {
                  if (result?.file) setState((prev) => ({ ...prev, files: [result.file, ...prev.files] }));
                  onUploaded?.(result);
                }}
                testId="folder-upload"
              />
            ) : (
              <p className="rounded-[12px] border border-dashed border-[color:var(--line-dashed)] px-3 py-3 text-[12.5px] text-ink-3" data-testid="folder-upload-read-only">
                This project's OneDrive connection can only read. Reconnect OneDrive (⋯ › Reconnect) to upload from here.
              </p>
            )}
          </div>
        </>
      )}
      {linked ? (
        <div className="flex items-center gap-1.5 border-t border-line bg-surface-2 px-4 py-2 text-[12px] text-ink-3 sm:px-5">
          <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Linked to Stage {linked.number} · {linked.stage.name}{row.depth > 0 ? ` → ${row.name}` : ''}
        </div>
      ) : null}
    </Panel>
  );
}

/** What the footer may say about the nightly scan: on only when OneDrive is on AND the job really ran lately. */
export function nightlyScanText(nightly) {
  if (!nightly) return 'Nightly auto-scan is off until OneDrive is set up';
  const failed = nightly.last_run?.status === 'failed';
  if (nightly.enabled) return `Nightly auto-scan on · ${nightly.schedule_label || 'every night 02:00'}${failed ? ' · last run failed' : ''}`;
  if (nightly.feature_on) return 'Nightly auto-scan not scheduled yet — IT adds it to the server (see Integrations setup)';
  return 'Nightly auto-scan is off until OneDrive is set up';
}

const folderName = (path) => String(path || '').split('/').pop();

function ActivityItem({ event }) {
  const folders = event.paths || [];
  return (
    <li className="flex items-start gap-2.5 border-b border-line-2 px-4 py-2.5 last:border-b-0 sm:px-5" data-testid={`files-activity-${event.id}`}>
      <Avatar name={event.user_name || '?'} size={28} className="shrink-0" />
      <span className="min-w-0 flex-1 text-[13px] leading-snug text-ink-2">
        <b className="font-semibold text-ink">{event.user_name || 'Someone'}</b>
        {event.action === 'upload' ? (
          <>
            {' uploaded '}
            <span className="font-semibold text-ink">{event.name}</span>
            {' to '}
            {folderName(event.path)}
          </>
        ) : (
          <span title={folders.join('\n')}>
            {` created ${folders.length} missing folder${folders.length === 1 ? '' : 's'}`}
          </span>
        )}
        <span className="block text-[12px] text-ink-3">{formatDateTime(event.at)}</span>
      </span>
      {event.action === 'upload'
        ? <FileUp className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-4" aria-label="Upload" />
        : <FolderPlus className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-4" aria-label="New folders" />}
    </li>
  );
}

export function ScanHistoryCard({ history = [], activity = [], nightly, className }) {
  // scans and OneDrive changes made from Sara, newest first
  const items = [
    ...history.map((scan) => ({ kind: 'scan', at: scan.scanned_at || '', scan })),
    ...(activity || []).map((event) => ({ kind: 'event', at: event.at || '', event })),
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const failed = nightly?.last_run?.status === 'failed';
  return (
    <Panel className={cn('overflow-hidden', className)} data-testid="files-scan-history" aria-labelledby="files-scan-history-title">
      <PanelHead title={<span id="files-scan-history-title">{activity?.length ? 'Scans and uploads' : 'Scan history'}</span>} meta="Ready folders" />
      {items.length ? (
        <ul>
          {items.map((item) => {
            if (item.kind === 'event') return <ActivityItem key={`event-${item.event.id}`} event={item.event} />;
            const { scan } = item;
            const nightlyScan = scan.trigger === 'nightly';
            const summary = scan.summary || {};
            return (
              <li key={scan.id} className="flex items-start gap-2.5 border-b border-line-2 px-4 py-2.5 last:border-b-0 sm:px-5">
                {nightlyScan ? (
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-surface-2 text-ink-3" aria-hidden="true"><RefreshCcw className="h-3.5 w-3.5" /></span>
                ) : (
                  <Avatar name={scan.scanned_by || '?'} size={28} className="shrink-0" />
                )}
                <span className="min-w-0 flex-1 text-[13px] leading-snug text-ink-2">
                  <b className="font-semibold text-ink">{nightlyScan ? 'Nightly auto-scan' : (scan.scanned_by || 'Someone')}</b>
                  {nightlyScan ? '' : ' scanned OneDrive'}
                  {scan.changes && !Object.values(scan.changes).some(Boolean) ? <span className="text-ink-3"> · no changes</span> : null}
                  <span className="block text-[12px] text-ink-3">{formatDateTime(scan.scanned_at)}</span>
                </span>
                <span className="shrink-0 text-[12.5px] font-semibold tabular-nums text-ink">{summary.ready ?? 0}/{summary.total ?? 0}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="px-4 py-4 text-[13px] text-ink-3 sm:px-5">No scan yet.</p>
      )}
      <div
        className={cn('flex items-center gap-1.5 border-t border-line bg-surface-2 px-4 py-2 text-[12px] text-ink-3 sm:px-5', failed && nightly?.enabled && 'text-status-amber-ink')}
        data-testid="files-nightly"
        data-state={nightly?.enabled ? 'on' : (nightly?.feature_on ? 'not-scheduled' : 'off')}
      >
        <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {nightlyScanText(nightly)}
      </div>
    </Panel>
  );
}
